import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, enableProdMode, ErrorHandler, importProvidersFrom, inject, LOCALE_ID } from '@angular/core';
import { environment } from '@environments/environment';
import { AppComponent } from './app/app.component';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { IonicRouteStrategy, IonicModule } from '@ionic/angular';
import { provideRouter, Router, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';
import { APP_ROUTES } from './app/app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { DateInterceptor } from './app/interceptors/date.interceptor';
import { SentryErrorInterceptor } from './app/interceptors/sentry-error.interceptor';
import { TokenInterceptor } from './app/interceptors/token.interceptor';
import { GlobalErrorHandler } from './app/services/global-error-handler';
import { throttleSentryEvent } from './app/services/sentry-event-throttle';
import { isMediaAbortRejection } from './app/services/sentry-media-abort-filter';
import { isInjectedWebViewScriptError } from './app/services/sentry-injected-script-filter';
import { attachErrorIdentity } from './app/services/sentry-error-identity';
import { dropNoisySpans } from './app/services/sentry-span-filter';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import ptBr from '@angular/common/locales/pt';
import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { DateTime } from 'luxon';
import { carregarConfig } from './app/config/remote-config.bootstrap';

registerLocaleData(ptBr);
DateTime.local().setLocale("pt-BR");

if (environment.production) {
  enableProdMode();
}

if (environment.production && environment.sentryDsn) {
  Sentry.init(
    {
      dsn: environment.sentryDsn,
      environment: 'production',
      integrations: [
        SentryAngular.browserTracingIntegration(),
      ],
      // Amostragem de performance (transações).
      //
      // Estava em 0.2, e a 20% este app sozinho produziu 229.513.869 spans em 30
      // dias (medição de 10/09/2026) — 100% dos spans da organização, a subir:
      // 10.001.090 nas últimas 24 h. A 0.05 a mesma amostra dá cerca de 460 mil
      // transações por mês, que continua a ser muito mais do que é preciso para
      // ler percentis com confiança.
      //
      // Esta linha corta o volume por quatro; o `beforeSendTransaction` abaixo
      // corta o que sobra por op. As duas coisas são independentes de propósito:
      // subir a taxa outra vez para investigar não traz de volta o ruído.
      tracesSampleRate: 0.05,
      // Ignora erros originados por extensões de terceiros injetadas na página (ex.: MetaMask),
      // que não têm relação com o código do app. `blob:` entra na lista porque
      // extensões criam Workers a partir de Blob — que herdam a origem da página
      // (blob:https://app.trei.no/...) e escapam do filtro chrome-extension://.
      // O app não gera script em blob, então nenhum erro nosso é perdido aqui.
      denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^blob:/i],
      ignoreErrors: [
        /MetaMask/i,
        // WebViews Android com apps injetores (ex.: SnapTube) expõem métodos nativos
        // via addJavascriptInterface; quando a chamada Java falha, o erro sobe pelo
        // window.onerror e é atribuído ao app. A origem é `app:///<anonymous>`, então
        // denyUrls não alcança.
        //
        // Mantido mesmo com o corte estrutural do `isInjectedWebViewScriptError`
        // abaixo: esta mensagem é específica o bastante para nenhum código nosso
        // a produzir, e não temos evento arquivado que confirme que a stack dela
        // carrega o símbolo da ponte. Enquanto não confirmar, os dois convivem.
        /Java bridge method invocation error/i,
      ],
      // Pipeline de saída, em três etapas e nesta ordem:
      //
      //   1. filtrar   — descarta o ruído conhecido antes de ele gastar assinatura
      //                  do throttle e ocupar o teto de eventos por sessão;
      //   2. throttle  — um erro em loop fora da zone do Angular (que não passa
      //                  pelo GlobalErrorHandler) consegue enviar dezenas de
      //                  eventos idênticos por minuto; colapsa repetições por
      //                  assinatura e limita o total por sessão;
      //   3. enriquecer — só o que vai mesmo ser enviado paga o custo de anexar
      //                  identidade da DOMException e stack cru.
      //
      // Inverter 1 e 2 deixaria o ruído consumir o orçamento do throttle;
      // enriquecer antes de filtrar seria trabalho perdido em evento descartado.
      // Só afeta eventos de erro; transações passam por `beforeSendTransaction`.
      beforeSend: (event, hint) => {
        if (isMediaAbortRejection(event, hint)) return null;
        if (isInjectedWebViewScriptError(event)) return null;

        const throttled = throttleSentryEvent(event);
        if (!throttled) return null;

        return attachErrorIdentity(throttled, hint);
      },
      // Contrapartida do `beforeSend` para o lado das transações: descarta os
      // spans que a instrumentação padrão cria e que ninguém lê (mark, measure,
      // resource.*, long-animation-frame), mantendo carregamento, navegação,
      // rota e chamada de API. Ver `sentry-span-filter.ts` para os números.
      beforeSendTransaction: dropNoisySpans,
    },
    // Inicializa o SDK Angular como sibling (necessário no @sentry/capacitor).
    SentryAngular.init
  );
}

function iniciarApp(): void {
  bootstrapApplication(AppComponent, {
  providers: [
    Calendar,
    provideHttpClient(
      withInterceptors([DateInterceptor]),
      withInterceptorsFromDi(),
      // Registrado por último para enxergar os headers adicionados pelo TokenInterceptor (ex.: Authorization).
      withInterceptors([SentryErrorInterceptor]),
    ),
    importProvidersFrom(BrowserModule, IonicModule.forRoot()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideRouter(APP_ROUTES, withComponentInputBinding()),
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'pt' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
    provideEnvironmentNgxMask(),
    // Async: o motor de animações (@angular/animations/browser) sai do bundle inicial e
    // é carregado sob demanda, na primeira animação. A troca custa que a primeira
    // transição de rota possa não animar — aceitável, já que ela cai justamente no
    // cold start que estamos tentando desbloquear.
    provideAnimationsAsync(),
    {
      // ErrorHandler global: captura exceções de runtime não tratadas e envia
      // ao Sentry com throttling (erros HTTP são tratados pelo interceptor).
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      // Rastreia navegação/performance de rotas.
      provide: SentryAngular.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [SentryAngular.TraceService],
      multi: true,
    },
  ]
})
    .catch(err => console.log(err));
}

/**
 * Tela de falha em DOM puro: este caminho roda ANTES do bootstrapApplication,
 * então não existe Angular, Ionic, componente nem router para renderizar nada.
 * O markup vive em index.html, oculto.
 */
function mostrarFalhaDeConfig(): void {
  const painel = document.getElementById('falha-config');
  if (!painel) return;

  if (!navigator.onLine) {
    const detalhe = document.getElementById('falha-config-detalhe');
    if (detalhe) detalhe.textContent = 'Você está sem conexão. Conecte-se e tente novamente.';
  }
  document.getElementById('falha-config-repetir')?.addEventListener('click', () => location.reload());

  // O display é definido AQUI, e não no style inline do elemento: um
  // `display:flex` de autor vence a regra `[hidden] { display: none }` da folha
  // do user agent, e o painel apareceria em toda carga — inclusive no caminho
  // de sucesso, por cima do app já bootado.
  painel.style.display = 'flex';
  painel.hidden = false;
}

// A configuração é aplicada ANTES do bootstrap para que o mapa de runtime já
// esteja preenchido quando a primeira URL for construída. O Sentry.init acima
// permanece antes disto de propósito: se o DSN dependesse deste fetch, um erro
// DENTRO dele aconteceria com o Sentry ainda desligado.
carregarConfig({
  appId: environment.appId,
  envId: environment.envId,
  configUrl: environment.configUrl,
}).then((resultado) => {
  if (!resultado.ok) {
    // Sem cache e sem rede: NÃO cai no bundle. Ver D1 do spec — o host embutido
    // é justamente o que pode estar quebrado.
    mostrarFalhaDeConfig();
    return;
  }
  iniciarApp();
});
